import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    X,
    Users,
    Calendar,
    Star,
    DollarSign,
    CheckCircle,
    User,
    Briefcase,
    Clock,
    ChevronRight,
    Sparkles,
    Building2,
    GraduationCap,
    History,
    Link as LinkIcon,
    MapPin,
} from "lucide-react";
import "./Traitementcandidatsmodal.css";

import { fetchCandidaturesByDemandeRequest } from "../../../../redux/actions/candidaturesActions";
import {
    fetchEntretiensByCandidatureRequest,
    createEntretienRequest,
} from "../../../../redux/actions/entretiensActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const NIVEAU_ETUDES_LABELS = {
    DIPLOME_BAC_2: "Diplôme (Bac + 2)",
    LICENCE_BAC_3: "Licence (Bac + 3)",
    MASTER_BAC_5: "Master (Bac + 5)",
    INGENIEUR_BAC_5: "Ingénieur (Bac + 5)",
    DOCTORAT_ET_PLUS: "Doctorat et plus",
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const getDisplayedPoste = (c) => {
    if (c?.autrePoste?.trim()) return c.autrePoste.trim();
    return c?.posteNom || c?.poste || "—";
};

const getDisplayedEtablissement = (c) => {
    if (c?.autreEtablissement?.trim()) return c.autreEtablissement.trim();
    return c?.etablissementNom || c?.etablissement || "—";
};

const getDisplayedNiveauEtude = (c) => {
    const raw = c?.niveauEtude;
    return NIVEAU_ETUDES_LABELS[raw] || raw || "—";
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("fr-FR");
};

const formatMode = (mode) => {
    if (mode === "EN_LIGNE") return "En ligne";
    if (mode === "PRESENTIEL") return "Présentiel";
    return mode || "—";
};

const getUserDisplayNameById = (users, userId) => {
    if (!userId) return "—";

    const user = (users || []).find((u) => Number(u.id) === Number(userId));
    if (!user) return `ID ${userId}`;

    if (user.fullName && String(user.fullName).trim()) {
        return user.fullName;
    }

    const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return full || `ID ${userId}`;
};

const getDemandeurDisplay = (users, demande) => {
    return getUserDisplayNameById(users, demande?.demandeurId);
};

const getPlanificateurDisplay = (users, entretien) => {
    return getUserDisplayNameById(users, entretien?.createdBy);
};

/* =============================================
   SUB-MODAL : Historique des entretiens
============================================= */
function HistoriqueEntretiensModal({ open, onClose, candidat, entretiens, users }) {
    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <History size={17} />
                        Historique des entretiens —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    {!entretiens || entretiens.length === 0 ? (
                        <div className="tcmEmptyCell">Aucun entretien planifié</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {entretiens.map((e) => (
                                <div
                                    key={e.id}
                                    style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                        padding: 12,
                                        background: "#f9fafb",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 12,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <div style={{ fontWeight: 600 }}>
                                            {e.type === "TECHNIQUE" ? "Entretien Technique" : "Entretien RH"}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                            Planifié par : {getPlanificateurDisplay(users, e)}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                                        <div><strong>Mode :</strong> {formatMode(e.mode)}</div>
                                        <div>
                                            <strong>{e.mode === "EN_LIGNE" ? "Lien visio" : "Lieu"} :</strong>{" "}
                                            {e.lieu || "—"}
                                        </div>
                                        <div><strong>Date :</strong> {e.date ? formatDate(e.date) : "—"}</div>
                                        <div><strong>Heure :</strong> {e.heure || "—"}</div>
                                        <div><strong>Commentaire :</strong> {e.commentaire || "—"}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Planifier Entretien
============================================= */
function PlanifierEntretienModal({ open, onClose, candidat, onSave }) {
    const [typeEntretien, setTypeEntretien] = useState("");
    const [dateEntretien, setDateEntretien] = useState("");
    const [heureEntretien, setHeureEntretien] = useState("");
    const [modeEntretien, setModeEntretien] = useState("");
    const [lieuEntretien, setLieuEntretien] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [step, setStep] = useState(1);
    const [generatingPlan, setGeneratingPlan] = useState(false);
    const [planIA, setPlanIA] = useState("");

    const reset = () => {
        setTypeEntretien("");
        setDateEntretien("");
        setHeureEntretien("");
        setModeEntretien("");
        setLieuEntretien("");
        setCommentaire("");
        setPlanIA("");
        setGeneratingPlan(false);
        setStep(1);
    };

    const handleGeneratePlanIA = async () => {
        if (!typeEntretien) return;

        setGeneratingPlan(true);
        try {
            await new Promise((r) => setTimeout(r, 700));
            const who = `${candidat.candidatPrenom} ${candidat.candidatNom}`;
            const role = getDisplayedPoste(candidat);

            setPlanIA(
                `Plan d’entretien (${typeEntretien}) — ${who}\n` +
                `Poste : ${role}\n\n` +
                `1) Présentation du contexte\n` +
                `2) Parcours du candidat\n` +
                `3) Questions ciblées ${typeEntretien === "TECHNIQUE" ? "techniques" : "RH"}\n` +
                `4) Disponibilité / motivation\n` +
                `5) Questions-réponses et clôture`
            );
        } finally {
            setGeneratingPlan(false);
        }
    };

    const handleNext = () => {
        if (!typeEntretien) return;
        setStep(2);
    };

    const handleSubmit = () => {
        if (!dateEntretien || !heureEntretien || !modeEntretien || !lieuEntretien) return;

        onSave({
            type: typeEntretien,
            mode: modeEntretien,
            lieu: lieuEntretien,
            date: dateEntretien,
            heure: heureEntretien,
            commentaire,
        });

        reset();
        onClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Calendar size={17} />
                        Planifier un entretien —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSteps">
                    <div className={`tcmStep ${step >= 1 ? "tcmStep--active" : ""}`}>
                        <div className="tcmStep__num">1</div>
                        <span>Type</span>
                    </div>
                    <ChevronRight size={14} className="tcmStep__sep" />
                    <div className={`tcmStep ${step >= 2 ? "tcmStep--active" : ""}`}>
                        <div className="tcmStep__num">2</div>
                        <span>Planification</span>
                    </div>
                </div>

                <div className="tcmSubModal__body">
                    {step === 1 && (
                        <div className="tcmStepContent">
                            <p className="tcmStepDesc">
                                Choisissez le type d'entretien à planifier.
                            </p>

                            <div className="tcmTypeCards">
                                <div
                                    className={`tcmTypeCard ${typeEntretien === "TECHNIQUE" ? "tcmTypeCard--selected" : ""}`}
                                    onClick={() => setTypeEntretien("TECHNIQUE")}
                                >
                                    <div className="tcmTypeCard__icon tcmTypeCard__icon--tech">
                                        <Briefcase size={22} />
                                    </div>
                                    <div className="tcmTypeCard__label">Entretien Technique</div>
                                    <div className="tcmTypeCard__desc">
                                        Évaluation des compétences techniques et métier
                                    </div>
                                    {typeEntretien === "TECHNIQUE" && (
                                        <div className="tcmTypeCard__check">
                                            <CheckCircle size={16} />
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`tcmTypeCard ${typeEntretien === "RH" ? "tcmTypeCard--selected" : ""}`}
                                    onClick={() => setTypeEntretien("RH")}
                                >
                                    <div className="tcmTypeCard__icon tcmTypeCard__icon--rh">
                                        <Users size={22} />
                                    </div>
                                    <div className="tcmTypeCard__label">Entretien RH</div>
                                    <div className="tcmTypeCard__desc">
                                        Évaluation de la personnalité, motivation et culture fit
                                    </div>
                                    {typeEntretien === "RH" && (
                                        <div className="tcmTypeCard__check">
                                            <CheckCircle size={16} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="tcmStepContent">
                            <div className="tcmEntretienTypeBadge tcmEntretienTypeBadge--selected">
                                {typeEntretien === "TECHNIQUE" ? (
                                    <>
                                        <Briefcase size={14} /> Entretien Technique
                                    </>
                                ) : (
                                    <>
                                        <Users size={14} /> Entretien RH
                                    </>
                                )}
                            </div>

                            {planIA && (
                                <div className="tcmAiPlanBox">
                                    <div className="tcmAiPlanBox__head">
                                        <Sparkles size={16} />
                                        <span>Plan d’entretien généré (IA)</span>
                                    </div>
                                    <pre className="tcmAiPlanBox__content">{planIA}</pre>
                                </div>
                            )}

                            <div className="tcmFormGrid">
                                <div className="tcmFormField tcmFormField--half">
                                    <label>Date *</label>
                                    <input
                                        type="date"
                                        value={dateEntretien}
                                        onChange={(e) => setDateEntretien(e.target.value)}
                                    />
                                </div>

                                <div className="tcmFormField tcmFormField--half">
                                    <label>Heure *</label>
                                    <input
                                        type="time"
                                        value={heureEntretien}
                                        onChange={(e) => setHeureEntretien(e.target.value)}
                                    />
                                </div>

                                <div className="tcmFormField">
                                    <label>Mode *</label>
                                    <select
                                        value={modeEntretien}
                                        onChange={(e) => {
                                            setModeEntretien(e.target.value);
                                            setLieuEntretien("");
                                        }}
                                    >
                                        <option value="">Sélectionner</option>
                                        <option value="EN_LIGNE">En ligne</option>
                                        <option value="PRESENTIEL">Présentiel</option>
                                    </select>
                                </div>

                                {modeEntretien === "EN_LIGNE" && (
                                    <div className="tcmFormField">
                                        <label>Lien visio *</label>
                                        <div className="tcmCellInline">
                                            <LinkIcon size={14} />
                                            <input
                                                type="url"
                                                placeholder="https://meet.google.com/..."
                                                value={lieuEntretien}
                                                onChange={(e) => setLieuEntretien(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {modeEntretien === "PRESENTIEL" && (
                                    <div className="tcmFormField">
                                        <label>Adresse / salle *</label>
                                        <div className="tcmCellInline">
                                            <MapPin size={14} />
                                            <input
                                                type="text"
                                                placeholder="Ex: Salle A - 2ème étage"
                                                value={lieuEntretien}
                                                onChange={(e) => setLieuEntretien(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="tcmFormField">
                                    <label>Commentaire</label>
                                    <textarea
                                        rows={3}
                                        placeholder="Instructions particulières..."
                                        value={commentaire}
                                        onChange={(e) => setCommentaire(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="tcmSubModal__footer tcmSubModal__footer--split">
                    <div className="tcmFooterLeft">
                        {step === 2 && (
                            <button
                                className="tcmBtn tcmBtn--ghost tcmBtn--ai"
                                type="button"
                                onClick={handleGeneratePlanIA}
                                disabled={generatingPlan}
                            >
                                <Sparkles size={15} />
                                {generatingPlan ? "Génération..." : "Plan d’entretien (IA)"}
                            </button>
                        )}
                    </div>

                    <div className="tcmFooterRight">
                        {step === 2 && (
                            <button className="tcmBtn tcmBtn--ghost" onClick={() => setStep(1)} type="button">
                                Retour
                            </button>
                        )}

                        <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                            Annuler
                        </button>

                        {step === 1 ? (
                            <button
                                className="tcmBtn tcmBtn--primary"
                                onClick={handleNext}
                                disabled={!typeEntretien}
                                type="button"
                            >
                                Suivant
                            </button>
                        ) : (
                            <button
                                className="tcmBtn tcmBtn--primary"
                                onClick={handleSubmit}
                                disabled={!dateEntretien || !heureEntretien || !modeEntretien || !lieuEntretien}
                                type="button"
                            >
                                <Calendar size={15} /> Planifier
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Évaluer Candidat
============================================= */
function EvaluerModal({ open, onClose, candidat, onSave }) {
    const [note, setNote] = useState(0);
    const [hoverNote, setHoverNote] = useState(0);
    const [commentaire, setCommentaire] = useState("");
    const [decision, setDecision] = useState("");
    const [competences, setCompetences] = useState({
        technique: 0,
        communication: 0,
        aptitude: 0,
        motivation: 0,
    });

    const handleSubmit = () => {
        if (!note || !decision) return;
        onSave({ note, commentaire, decision, competences });
        setNote(0);
        setCommentaire("");
        setDecision("");
        setCompetences({ technique: 0, communication: 0, aptitude: 0, motivation: 0 });
        onClose();
    };

    const handleClose = () => {
        setNote(0);
        setHoverNote(0);
        setCommentaire("");
        setDecision("");
        setCompetences({ technique: 0, communication: 0, aptitude: 0, motivation: 0 });
        onClose();
    };

    if (!open || !candidat) return null;

    const criterias = [
        { key: "technique", label: "Compétences techniques" },
        { key: "communication", label: "Communication" },
        { key: "aptitude", label: "Aptitude au poste" },
        { key: "motivation", label: "Motivation" },
    ];

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Star size={17} />
                        Évaluation —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Note globale *</div>
                        <div className="tcmStars">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`tcmStar ${(hoverNote || note) >= star ? "tcmStar--filled" : ""}`}
                                    onMouseEnter={() => setHoverNote(star)}
                                    onMouseLeave={() => setHoverNote(0)}
                                    onClick={() => setNote(star)}
                                >
                                    <Star size={28} />
                                </button>
                            ))}
                            {note > 0 && (
                                <span className="tcmStarLabel">
                                    {["", "Insuffisant", "Passable", "Bien", "Très bien", "Excellent"][note]}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Évaluation par critères</div>
                        <div className="tcmCriterias">
                            {criterias.map((c) => (
                                <div key={c.key} className="tcmCriteria">
                                    <div className="tcmCriteria__label">{c.label}</div>
                                    <div className="tcmCriteria__stars">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                className={`tcmStarSm ${
                                                    competences[c.key] >= star ? "tcmStarSm--filled" : ""
                                                }`}
                                                onClick={() =>
                                                    setCompetences((prev) => ({ ...prev, [c.key]: star }))
                                                }
                                            >
                                                <Star size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Commentaire</div>
                        <textarea
                            className="tcmTextarea"
                            rows={3}
                            placeholder="Points forts, points d'amélioration, observations..."
                            value={commentaire}
                            onChange={(e) => setCommentaire(e.target.value)}
                        />
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Décision *</div>
                        <div className="tcmDecisions">
                            {[
                                { value: "Retenu", color: "#4CAF50", label: "✅ Retenu" },
                                { value: "En réserve", color: "#ff9800", label: "⏳ En réserve" },
                                { value: "Refusé", color: "#f44336", label: "❌ Refusé" },
                            ].map((d) => (
                                <div
                                    key={d.value}
                                    className={`tcmDecisionCard ${
                                        decision === d.value ? "tcmDecisionCard--selected" : ""
                                    }`}
                                    style={
                                        decision === d.value
                                            ? { borderColor: d.color, background: `${d.color}12` }
                                            : {}
                                    }
                                    onClick={() => setDecision(d.value)}
                                >
                                    {d.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={handleSubmit}
                        disabled={!note || !decision}
                        type="button"
                    >
                        <Star size={15} /> Enregistrer l'évaluation
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Offre Salariale
============================================= */
function OffreSalarialeModal({ open, onClose, candidat, onSave }) {
    const [salaireBase, setSalaireBase] = useState("");
    const [primes, setPrimes] = useState("");
    const [avantages, setAvantages] = useState("");
    const [dateDebut, setDateDebut] = useState("");
    const [periodeEssai, setPeriodeEssai] = useState("");
    const [noteComplementaire, setNoteComplementaire] = useState("");
    const [envoyerEmail, setEnvoyerEmail] = useState(true);

    const handleSubmit = () => {
        if (!salaireBase || !dateDebut) return;
        onSave({
            salaireBase,
            primes,
            avantages,
            dateDebut,
            periodeEssai,
            noteComplementaire,
            envoyerEmail,
        });
        setSalaireBase("");
        setPrimes("");
        setAvantages("");
        setDateDebut("");
        setPeriodeEssai("");
        setNoteComplementaire("");
        setEnvoyerEmail(true);
        onClose();
    };

    const handleClose = () => {
        setSalaireBase("");
        setPrimes("");
        setAvantages("");
        setDateDebut("");
        setPeriodeEssai("");
        setNoteComplementaire("");
        setEnvoyerEmail(true);
        onClose();
    };

    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <DollarSign size={17} />
                        Offre salariale —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmOffrePreview">
                        <div className="tcmOffrePreview__icon">
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <div className="tcmOffrePreview__name">
                                {candidat.candidatPrenom} {candidat.candidatNom}
                            </div>
                            <div className="tcmOffrePreview__post">{getDisplayedPoste(candidat)}</div>
                            <div className="tcmOffrePreview__email">{candidat.candidatEmail}</div>
                        </div>
                    </div>

                    <div className="tcmFormGrid">
                        <div className="tcmFormField tcmFormField--half">
                            <label>Salaire de base brut (MAD/mois) *</label>
                            <input
                                type="number"
                                placeholder="Ex: 12000"
                                value={salaireBase}
                                onChange={(e) => setSalaireBase(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField tcmFormField--half">
                            <label>Primes et bonus (MAD/an)</label>
                            <input
                                type="number"
                                placeholder="Ex: 24000"
                                value={primes}
                                onChange={(e) => setPrimes(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField">
                            <label>Avantages</label>
                            <input
                                type="text"
                                placeholder="Ex: Mutuelle, voiture de fonction..."
                                value={avantages}
                                onChange={(e) => setAvantages(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField tcmFormField--half">
                            <label>Date de prise de poste *</label>
                            <input
                                type="date"
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField tcmFormField--half">
                            <label>Période d'essai</label>
                            <select value={periodeEssai} onChange={(e) => setPeriodeEssai(e.target.value)}>
                                <option value="">Sélectionner...</option>
                                <option value="1 mois">1 mois</option>
                                <option value="2 mois">2 mois</option>
                                <option value="3 mois">3 mois</option>
                                <option value="6 mois">6 mois</option>
                            </select>
                        </div>
                        <div className="tcmFormField">
                            <label>Note complémentaire</label>
                            <textarea
                                rows={3}
                                placeholder="Conditions particulières..."
                                value={noteComplementaire}
                                onChange={(e) => setNoteComplementaire(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField">
                            <div className="tcmCheckRow">
                                <input
                                    type="checkbox"
                                    id="sendEmail"
                                    checked={envoyerEmail}
                                    onChange={(e) => setEnvoyerEmail(e.target.checked)}
                                />
                                <label htmlFor="sendEmail" className="tcmCheckLabel">
                                    Envoyer l'offre par email au candidat ({candidat.candidatEmail})
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={handleSubmit}
                        disabled={!salaireBase || !dateDebut}
                        type="button"
                    >
                        <DollarSign size={15} />
                        {envoyerEmail ? "Envoyer l'offre" : "Enregistrer l'offre"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Confirmer Terminé
============================================= */
function TerminerModal({ open, onClose, candidat, onConfirm }) {
    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel tcmSubModal__panel--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <CheckCircle size={17} />
                        Finaliser le recrutement
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmTerminerContent">
                        <div className="tcmTerminerIcon">
                            <CheckCircle size={40} color="#4CAF50" />
                        </div>
                        <p className="tcmTerminerText">
                            Confirmez-vous que cette étape du processus de candidature de{" "}
                            <strong>
                                {candidat.candidatPrenom} {candidat.candidatNom}
                            </strong>{" "}
                            pour le poste de <strong>{getDisplayedPoste(candidat)}</strong> est validée ?
                        </p>
                        <p className="tcmTerminerSubText">
                            Cette action permettra de passer aux prochaines étapes.
                        </p>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                        Annuler
                    </button>
                    <button className="tcmBtn tcmBtn--success" onClick={onConfirm} type="button">
                        <CheckCircle size={15} /> Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   MAIN : TraitementCandidatsModal
============================================= */
export default function TraitementCandidatsModal({ open, onClose, demande }) {
    const dispatch = useDispatch();

    const safeDemande = demande || {};
    const demandeId = safeDemande.id;

    const candidaturesState = useSelector((state) => state.candidatures || {});
    const candidaturesRedux = candidaturesState.candidatures || [];
    const loading = !!candidaturesState.loading;
    const error = candidaturesState.error;

    const entretiensState = useSelector((state) => state.entretiens || {});
    const entretiensByCandidature = entretiensState.entretiensByCandidature || {};

    const userState = useSelector((state) => state.user || {});
    const users = userState.users || [];

    const [candidats, setCandidats] = useState([]);

    const [planifierModal, setPlanifierModal] = useState({ open: false, candidat: null });
    const [evaluerModal, setEvaluerModal] = useState({ open: false, candidat: null });
    const [offreModal, setOffreModal] = useState({ open: false, candidat: null });
    const [terminerModal, setTerminerModal] = useState({ open: false, candidat: null });
    const [historiqueModal, setHistoriqueModal] = useState({ open: false, candidat: null, entretiens: [] });

    useEffect(() => {
        if (!open || !demandeId) return;
        dispatch(fetchCandidaturesByDemandeRequest(demandeId));
        dispatch(getAllUsersRequest());
    }, [dispatch, open, demandeId]);

    const candidatsApprouves = useMemo(() => {
        return (candidaturesRedux || []).filter(
            (c) => normalizeStatus(c.statut) === "validée"
        );
    }, [candidaturesRedux]);

    useEffect(() => {
        candidatsApprouves.forEach((c) => {
            dispatch(fetchEntretiensByCandidatureRequest(c.id));
        });
    }, [dispatch, candidatsApprouves]);

    useEffect(() => {
        setCandidats((prev) => {
            const localMap = new Map(prev.map((item) => [item.id, item]));
            return candidatsApprouves.map((item) => {
                const existing = localMap.get(item.id);
                return {
                    ...item,
                    evaluation: existing?.evaluation || null,
                    offreSalariale: existing?.offreSalariale || null,
                    statutTraitement: existing?.statutTraitement || "En attente",
                };
            });
        });
    }, [candidatsApprouves]);

    useEffect(() => {
        const isAnyModalOpen =
            open ||
            planifierModal.open ||
            evaluerModal.open ||
            offreModal.open ||
            terminerModal.open ||
            historiqueModal.open;

        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [open, planifierModal.open, evaluerModal.open, offreModal.open, terminerModal.open, historiqueModal.open]);

    const updateCandidat = (id, updates) => {
        setCandidats((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    };

    const getEntretienStats = (candidatureId) => {
        const list = entretiensByCandidature[candidatureId] || [];
        const tech = list.filter((e) => e.type === "TECHNIQUE").length;
        const rh = list.filter((e) => e.type === "RH").length;
        return { tech, rh, total: list.length };
    };

    const handlePlanifierSave = (data) => {
        const c = planifierModal.candidat;
        if (!c) return;

        dispatch(
            createEntretienRequest({
                type: data.type,
                mode: data.mode,
                lieu: data.lieu,
                date: data.date,
                heure: data.heure,
                commentaire: data.commentaire,
                candidatureId: c.id,
            })
        );

        setTimeout(() => {
            dispatch(fetchEntretiensByCandidatureRequest(c.id));
        }, 300);

        updateCandidat(c.id, {
            statutTraitement: c.statutTraitement === "En attente" ? "En cours" : c.statutTraitement,
        });

        showSwal({
            icon: "success",
            text: `Entretien planifié pour ${c.candidatPrenom} ${c.candidatNom}`,
            timer: 1600,
            showConfirmButton: false,
        });
    };

    const handleEvaluerSave = (data) => {
        const c = evaluerModal.candidat;
        if (!c) return;
        updateCandidat(c.id, {
            evaluation: data,
            statutTraitement: "Évalué",
        });
    };

    const handleOffreSave = (data) => {
        const c = offreModal.candidat;
        if (!c) return;

        updateCandidat(c.id, {
            offreSalariale: data,
            statutTraitement: "Offre envoyée",
        });

        showSwal({
            icon: "success",
            text: `Offre salariale ${data.envoyerEmail ? "envoyée" : "enregistrée"} pour ${c.candidatPrenom} ${c.candidatNom}`,
            timer: 1800,
            showConfirmButton: false,
        });
    };

    const handleTerminer = () => {
        const c = terminerModal.candidat;
        if (!c) return;

        updateCandidat(c.id, { statutTraitement: "Recruté ✓" });
        setTerminerModal({ open: false, candidat: null });

        showSwal({
            icon: "success",
            text: `Le recrutement de ${c.candidatPrenom} ${c.candidatNom} est finalisé.`,
            timer: 1800,
            showConfirmButton: false,
        });
    };

    const getStatutBadge = (statut) => {
        const map = {
            "En attente": { bg: "#fef3c7", color: "#92400e" },
            "En cours": { bg: "#dbeafe", color: "#1e40af" },
            "Évalué": { bg: "#e0e7ff", color: "#3730a3" },
            "Offre envoyée": { bg: "#d1fae5", color: "#065f46" },
            "Recruté ✓": { bg: "#d1fae5", color: "#065f46" },
        };
        const style = map[statut] || { bg: "#f3f4f6", color: "#374151" };

        return (
            <span className="tcmStatutPill" style={{ background: style.bg, color: style.color }}>
                {statut}
            </span>
        );
    };

    if (!open) return null;

    return (
        <>
            <div className="tcmModal" onMouseDown={onClose} role="dialog" aria-modal="true">
                <div className="tcmModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                    <div className="tcmModal__header">
                        <div className="tcmModal__title">
                            <Users size={18} />
                            Traitement des candidats —{" "}
                            <span className="tcmModal__titlePoste">{getDisplayedPoste(safeDemande)}</span>
                        </div>
                        <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="tcmModal__body">
                        <div className="tcmDemandeInfo">
                            <div className="tcmDemandeInfo__item">
                                <User size={14} />
                                <span>
                                    <strong>Demandeur :</strong> {getDemandeurDisplay(users, safeDemande)}
                                </span>
                            </div>

                            <div className="tcmDemandeInfo__item">
                                <Briefcase size={14} />
                                <span>
                                    <strong>Poste :</strong> {getDisplayedPoste(safeDemande)}
                                </span>
                            </div>

                            <div className="tcmDemandeInfo__item">
                                <Clock size={14} />
                                <span>
                                    <strong>Statut demande :</strong>{" "}
                                    <span className="tcmDemandeInfo__status">
                                        {safeDemande.statut || safeDemande.status || "—"}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className="tcmSection__title">
                            <User size={15} />
                            Candidats approuvés par le manager ({candidats.length})
                        </div>

                        <div className="tcmTableWrap">
                            <table className="tcmTable">
                                <thead>
                                <tr>
                                    <th>Candidat</th>
                                    <th>Poste</th>
                                    <th>Niveau d'étude</th>
                                    <th>Établissement</th>
                                    <th>Entretien Tech.</th>
                                    <th>Entretien RH</th>
                                    <th>Évaluation</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={9} className="tcmEmptyCell">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : candidats.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="tcmEmptyCell">
                                            Aucun candidat approuvé pour cette demande
                                        </td>
                                    </tr>
                                ) : (
                                    candidats.map((candidat) => {
                                        const stats = getEntretienStats(candidat.id);

                                        return (
                                            <tr key={candidat.id}>
                                                <td>
                                                    <div>
                                                        <div className="tcmStrong">
                                                            {candidat.candidatPrenom} {candidat.candidatNom}
                                                        </div>
                                                        <div className="tcmMuted">
                                                            {candidat.candidatEmail || "—"}
                                                        </div>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="tcmCellInline">
                                                        <Briefcase size={13} />
                                                        <span className="tcmStrong">
                                                                {getDisplayedPoste(candidat)}
                                                            </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="tcmCellInline">
                                                        <GraduationCap size={13} />
                                                        <span className="tcmMuted">
                                                                {getDisplayedNiveauEtude(candidat)}
                                                            </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="tcmCellInline">
                                                        <Building2 size={13} />
                                                        <span className="tcmMuted">
                                                                {getDisplayedEtablissement(candidat)}
                                                            </span>
                                                    </div>
                                                </td>

                                                <td>
                                                        <span className="tcmEntretienBadge">
                                                            Tech: {stats.tech}
                                                        </span>
                                                </td>

                                                <td>
                                                        <span className="tcmEntretienBadge">
                                                            RH: {stats.rh}
                                                        </span>
                                                </td>

                                                <td>
                                                    {candidat.evaluation ? (
                                                        <div className="tcmEvalStars">
                                                            {[1, 2, 3, 4, 5].map((s) => (
                                                                <Star
                                                                    key={s}
                                                                    size={13}
                                                                    fill={candidat.evaluation.note >= s ? "#f59e0b" : "none"}
                                                                    color={candidat.evaluation.note >= s ? "#f59e0b" : "#d1d5db"}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="tcmMuted">—</span>
                                                    )}
                                                </td>

                                                <td>{getStatutBadge(candidat.statutTraitement)}</td>

                                                <td className="is-right">
                                                    <div className="tcmRowActions">
                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--calendar"
                                                            title="Planifier un entretien"
                                                            type="button"
                                                            onClick={() => setPlanifierModal({ open: true, candidat })}
                                                        >
                                                            <Calendar size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn"
                                                            title="Historique des entretiens"
                                                            type="button"
                                                            onClick={() =>
                                                                setHistoriqueModal({
                                                                    open: true,
                                                                    candidat,
                                                                    entretiens: entretiensByCandidature[candidat.id] || [],
                                                                })
                                                            }
                                                        >
                                                            <History size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--star"
                                                            title="Évaluer le candidat"
                                                            type="button"
                                                            onClick={() => setEvaluerModal({ open: true, candidat })}
                                                        >
                                                            <Star size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--dollar"
                                                            title="Offre salariale"
                                                            type="button"
                                                            onClick={() => setOffreModal({ open: true, candidat })}
                                                        >
                                                            <DollarSign size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--check"
                                                            title="Validé"
                                                            type="button"
                                                            onClick={() => setTerminerModal({ open: true, candidat })}
                                                            disabled={candidat.statutTraitement === "Recruté ✓"}
                                                        >
                                                            <CheckCircle size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>

                        {!!error && (
                            <div className="tcmWarning" style={{ marginTop: 10 }}>
                                {String(error)}
                            </div>
                        )}
                    </div>

                    <div className="tcmModal__footer">
                        <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                            Fermer
                        </button>
                    </div>
                </div>
            </div>

            <PlanifierEntretienModal
                open={planifierModal.open}
                onClose={() => setPlanifierModal({ open: false, candidat: null })}
                candidat={planifierModal.candidat}
                onSave={handlePlanifierSave}
            />

            <HistoriqueEntretiensModal
                open={historiqueModal.open}
                onClose={() => setHistoriqueModal({ open: false, candidat: null, entretiens: [] })}
                candidat={historiqueModal.candidat}
                entretiens={historiqueModal.entretiens}
                users={users}
            />

            <EvaluerModal
                open={evaluerModal.open}
                onClose={() => setEvaluerModal({ open: false, candidat: null })}
                candidat={evaluerModal.candidat}
                onSave={handleEvaluerSave}
            />

            <OffreSalarialeModal
                open={offreModal.open}
                onClose={() => setOffreModal({ open: false, candidat: null })}
                candidat={offreModal.candidat}
                onSave={handleOffreSave}
            />

            <TerminerModal
                open={terminerModal.open}
                onClose={() => setTerminerModal({ open: false, candidat: null })}
                candidat={terminerModal.candidat}
                onConfirm={handleTerminer}
            />
        </>
    );
}